import { css, html, LitElement } from 'lit-element';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { dispatchCustomEvent } from '../../utils/custom-event-dispatcher.js';
import { convertToPixel } from '../../utils/rem-to-pixel-convertor.js';
import { SmoothScroll } from '../../utils/smooth-scroll.js';

/**
 * Component that allows to select from a set of values by swiping.
 * Comes with a drawer component.
 *
 * @prop {Array} items - The total items of scrollpicker. The number of scrollers is decided by
 * the length of this array. What to show in each scroller is decided by
 * the arrays within the items array.
 * @attr {Array} items - The total items of scrollpicker. The number of scrollers is decided by
 * the length of this array. What to show in each scroller is decided by
 * the arrays within the items array.
 * @prop {Boolean} visible - The visible property of scrollpicker. Set this to true or false to
 * open/close the drawer within. Also the visible changed event can be
 * listened when visible is set to false to remove scrollpicker from DOM
 * when not required.
 * @attr {Boolean} visible - The visible property of scrollpicker. Set this to true or false to
 * open/close the drawer within. Also the visible changed event can be
 * listened when visible is set to false to remove scrollpicker from DOM
 * when not required.
 * @prop {Array} values - The selected values of scrollpicker. Set this array to scroll to those
 * values.
 * @attr {Array} values - The selected values of scrollpicker. Set this array to scroll to those
 * values.
 * @prop {Array} scrollerStyles - The individual styles for each row of each scroller.
 * @attr {Array} scrollerStyles - The individual styles for each row of each scroller.
 * @prop {Boolean} scroll - Set this true to scroll to the values set in the array.
 * @attr {Boolean} scroll - Set this true to scroll to the values set in the array.
 *
 */

export class SHScrollpicker extends LitElement {
  static get properties() {
    return {
      items: { type: Array },
      visible: { type: Boolean, reflect: true },
      values: { type: Array, reflect: true },
      scrollerStyles: { type: Array, reflect: true },
      scroll: { type: Boolean, reflect: true },
    };
  }
  static get styles() {
    return [
      css`
        :host * {
          box-sizing: border-box;
        }
        .main-container {
          height: 12.5rem;
          position: relative;
          transition: 0s all linear !important;
        }
        .scrollers-container {
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
          height: 100%;
          transition: 0s all linear !important;
        }
        .scroll-container {
          height: 100%;
          overflow-y: auto;
          scroll-snap-type: y mandatory;
          transition: 0s all linear !important;
          scroll-behavior: smooth;
          scrollbar-width: none;
          width: fit-content;
        }
        .scroll-container::-webkit-scrollbar {
          display: none;
        }
        .row {
          height: 2.5rem;
          width: 3.25rem;
          font: var(--body-1);
          color: var(--text-secondary);
          display: flex;
          justify-content: center;
          align-items: center;
          scroll-snap-align: start;
          box-sizing: border-box;
          word-break: keep-all;
          padding: 0.5rem 1rem;
          transition: 0s all linear !important;
        }
        .selector {
          position: absolute;
          top: 5rem;
          border-top-color: rgba(var(--ui-1), var(--opacity-6));
          border-bottom-color: rgba(var(--ui-1), var(--opacity-6));
          border-left-color: transparent;
          border-right-color: transparent;
          border-style: solid;
          border-width: 0.0625rem;
          height: 2.5rem;
          width: 100%;
          box-sizing: border-box;
          pointer-events: none;
          transition: 0s all linear !important;
        }
        .row.selected {
          color: var(--text-primary);
        }
        .row.no-click {
          width: 100%;
          padding: 0;
        }
      `,
    ];
  }
  constructor() {
    super();
    this.items = [];
    this.scrollerStyles = [];
    this.values = [];
    this.rowClicklistener = {
      handleEvent(e) {
        e.target.previousElementSibling.previousElementSibling.scrollIntoView({
          behavior: 'smooth',
        });
      },
      passive: true,
    };
    this.values = [];
    this._previousValues = [];
  }
  connectedCallback() {
    super.connectedCallback();
    this.resizeObserver = new ResizeObserver(() => {
      this.requestUpdate();
    });
    this.resizeObserver.observe(this);
    SmoothScroll.enable();
  }
  render() {
    return html`<sh-drawer
      position="bottom"
      class="point5-vertical-padding initial-slide-on-connect"
      .visible="${this.visible}"
      @transitionend="${(e) => {
        const drawer = e.target;
        if (!drawer.visible) {
          this.visible = false;
        }
      }}"
      ><div class="main-container">
        <div class="selector"></div>
        <div class="scrollers-container">${this._getScrollerDOM()}</div>
      </div></sh-drawer
    >`;
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    dispatchAttributeChangedEvent(this, changedProperties, ['visible', 'scroll']);
    if (changedProperties.has('values') && this.values && this.values.length > 0) {
      if (JSON.stringify(this._previousValues) !== JSON.stringify(this.values)) {
        this._previousValues = this.values;
        dispatchCustomEvent(this, 'values-changed');
      }
    }
    if (changedProperties.has('scroll') && this.scroll) {
      this.scrollToValues();
    }
  }
  disconnectedCallback() {
    this.resizeObserver.disconnect();
    super.disconnectedCallback();
  }
  /**
   * Function to scroll to the values specified
   * in the `values` array
   * @param {Array} values - the array of specified values
   * to which the scrollpicker has to scroll.
   */
  async scrollToValues(values = this.values) {
    if (values.length !== this.items.length) {
      console.warn('Items and values array should have same length');
    }
    await this.updateComplete;
    this._scrollAllScrollersToValues(values);
    this.scroll = false;
  }
  _getScrollerDOM() {
    let DOM = [];
    if (this.items && this.items.length > 0) {
      for (let i = 0; i < this.items.length; i++) {
        const item = this.items[i];
        DOM.push(html`<div
          class="scroll-container"
          @scroll="${(e) => {
            this._scrollerScrollTasks(e);
          }}"
        >
          <div class="row no-click"></div>
          <div class="row no-click"></div>
          ${this._getScrollDOMArray(item, i)}
          <div class="row no-click"></div>
          <div class="row no-click"></div>
        </div>`);
      }
      return DOM;
    }
  }
  _scrollerScrollTasks(e) {
    clearTimeout(e.target._scrollCorrectTimeout);
    const scroller = e.target;
    scroller.setAttribute('is-scrolling', '');
    scroller._scrollCorrectTimeout = setTimeout(
      this._scrollerScrollEndTasks.bind(this),
      300,
      scroller
    );
  }
  _setScrollerSelectedValue(scroller) {
    const ROW_HEIGHT_IN_REM = 2.5;
    const scrollerIndex = Array.from(scroller.parentNode.children).indexOf(scroller);
    const scrollTop = scroller.scrollTop;
    const rowHeightInPx = convertToPixel(ROW_HEIGHT_IN_REM);
    scroller._selectedValue = this.items[scrollerIndex][Math.round(scrollTop / rowHeightInPx)];
  }
  _updateValues() {
    const scrollers = Array.from(this.shadowRoot.querySelector('.scrollers-container').children);
    const newValues = [];
    for (let i = 0; i < scrollers.length; i++) {
      const scroller = scrollers[i];
      this._setScrollerSelectedValue(scroller);
      newValues[i] = scroller._selectedValue;
    }
    const newValuesStr = newValues.join('');
    const valuesStr = this.values.join('');
    if (newValuesStr !== valuesStr) {
      this.values = newValues;
    }
  }
  _scrollerScrollEndTasks(scroller) {
    const ROW_HEIGHT_IN_REM = 2.5;
    const scrollTop = scroller.scrollTop;
    const rowHeightInPx = convertToPixel(ROW_HEIGHT_IN_REM);
    if (this._scrollerHasNotStoppedAtMiddle(scrollTop, rowHeightInPx)) {
      this._nudgeScroller(scroller, scrollTop);
    } else {
      scroller.removeAttribute('is-scrolling');
      scroller.blur();
      if (this._hasAllScrollersStopped()) {
        this._updateValues();
      }
    }
  }
  _nudgeScroller(scroller, scrollTop) {
    scroller.scrollTop = scrollTop;
  }
  _scrollerHasNotStoppedAtMiddle(scrollTop, rowHeight) {
    return scrollTop % rowHeight !== 0;
  }
  _hasAllScrollersStopped() {
    const scrollers = Array.from(this.shadowRoot.querySelector('.scrollers-container').children);
    let scrollingScrollerCount = 0;
    for (let i = 0; i < scrollers.length; i++) {
      const scroller = scrollers[i];
      if (scroller.hasAttribute('is-scrolling')) {
        ++scrollingScrollerCount;
        break;
      }
    }
    return scrollingScrollerCount === 0;
  }
  _scrollAllScrollersToValues(values) {
    const scrollers = Array.from(this.shadowRoot.querySelector('.scrollers-container').children);
    let staticScrollerCount = 0;
    for (let i = 0; i < values.length; i++) {
      const ROW_HEIGHT_IN_REM = 2.5;
      const value = values[i];
      const valueIndex = this._getValueIndexForColumn(i, value);
      const rowHeightInPx = convertToPixel(ROW_HEIGHT_IN_REM);
      const selectedValueScrollTop = valueIndex * rowHeightInPx;
      const scroller = scrollers[i];
      if (scroller.scrollTop !== selectedValueScrollTop) {
        this._setScrollerScrollTop(selectedValueScrollTop, scroller);
      } else {
        ++staticScrollerCount;
      }
    }
    if (staticScrollerCount === scrollers.length) {
      this._updateValues();
    }
  }
  _getValueIndexForColumn(scrollerIndex, selectedValue) {
    return this.items[scrollerIndex].indexOf(selectedValue);
  }
  _setScrollerScrollTop(scrollTop, scroller) {
    scroller.scrollTop = scrollTop;
  }
  _getScrollDOMArray(item, scrollerIndex) {
    const itemLength = item.length;
    const itemDOM = [];
    for (let i = 0; i < itemLength; i++) {
      itemDOM.push(
        html`<div
          class="row
            ${this.values[scrollerIndex] && this.values[scrollerIndex] === item[i]
            ? ' selected'
            : ''}"
          @click="${this.rowClicklistener}"
          data-value="${item[i]}"
          style="${this.scrollerStyles[scrollerIndex] ? this.scrollerStyles[scrollerIndex] : ''}"
        >
          ${item[i]}
        </div>`
      );
    }
    return itemDOM;
  }
}
customElements.define('sh-scrollpicker', SHScrollpicker);
